import React from 'react';
import { Group, Line, Circle, Rect, Text } from 'react-konva';
import { useEditorStore } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  getTrayMediaConfig,
  calculateTrayMaxCables,
  calculateTrayFillStatus,
} from '../../utils/traySegregation.logic';

interface TraysLayerProps {
  ghostPos: { x: number; y: number } | null;
  dcCode: string;
}

export const TraysLayer: React.FC<TraysLayerProps> = ({ ghostPos, dcCode }) => {
  const {
    trayNodes,
    trayEdges,
    stageScale,
    drawingTrayFromId,
    tool,
    trayDrawType,
    selectedEdgeId,
    setSelectedEdgeId,
    setSelectedIds,
    deleteTrayEdge,
    deleteTrayNode,
    addTrayEdge,
    setDrawingTrayFromId,
  } = useEditorStore(
    useShallow(state => ({
      trayNodes: state.trayNodes,
      trayEdges: state.trayEdges,
      stageScale: state.stageScale,
      drawingTrayFromId: state.drawingTrayFromId,
      tool: state.tool,
      trayDrawType: state.trayDrawType,
      selectedEdgeId: state.selectedEdgeId,
      setSelectedEdgeId: state.setSelectedEdgeId,
      setSelectedIds: state.setSelectedIds,
      deleteTrayEdge: state.deleteTrayEdge,
      deleteTrayNode: state.deleteTrayNode,
      addTrayEdge: state.addTrayEdge,
      setDrawingTrayFromId: state.setDrawingTrayFromId,
    }))
  );

  const { activeWorkspaceId } = useWorkspace();

  const { data } = useQuery({
    queryKey: ['trayOccupancy', dcCode, activeWorkspaceId],
    queryFn: async () => {
      const res = await api.get(`/api/datacenters/${dcCode}/trays/occupancy`, {
        params: { workspace: activeWorkspaceId },
      });
      return res.data;
    },
    refetchInterval: 10000,
  });

  const edgeCounts = data?.edge_counts || {};
  const activeMediaConfig = getTrayMediaConfig(trayDrawType);

  const handleEdgeClick = (edgeId: string, e: any) => {
    e.cancelBubble = true;
    if (tool === 'erase') {
      deleteTrayEdge(edgeId);
    } else if (tool === 'select') {
      setSelectedEdgeId(edgeId);
      setSelectedIds([]); // deselect racks so tray properties open in sidebar
    }
  };

  const handleNodeClick = (nodeId: string, e: any) => {
    e.cancelBubble = true;
    if (tool === 'erase') {
      deleteTrayNode(nodeId);
    } else if (tool === 'draw_tray') {
      if (drawingTrayFromId && drawingTrayFromId !== nodeId) {
        const media = trayDrawType;
        const config = getTrayMediaConfig(media);
        addTrayEdge({
          id: `edge-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          source: drawingTrayFromId,
          target: nodeId,
          color: config.color,
          type: media,
          mediaType: media,
          trayType: config.defaultType,
          widthM: 0.3,
          maxCables: calculateTrayMaxCables(0.3, media),
        });
        setDrawingTrayFromId(nodeId);
      } else if (!drawingTrayFromId) {
        setDrawingTrayFromId(nodeId);
      }
    }
  };

  return (
    <Group listening={tool === 'select' || tool === 'erase' || tool === 'draw_tray'}>
      {Object.values(trayEdges).map(edge => {
        const source = trayNodes[edge.source];
        const target = trayNodes[edge.target];
        if (!source || !target) return null;

        const key1 = `${edge.source}_${edge.target}`;
        const key2 = `${edge.target}_${edge.source}`;
        const count = edgeCounts[key1] || edgeCounts[key2] || edge.usedCables || 0;

        const mediaConfig = getTrayMediaConfig(edge.mediaType || edge.type);
        const widthM = edge.widthM || 0.3;
        const maxCables = edge.maxCables || calculateTrayMaxCables(widthM, mediaConfig.media);
        const fillStatus = calculateTrayFillStatus(count, maxCables);
        const isSelected = selectedEdgeId === edge.id;

        // Visual width scaled by physical width
        const baseWidth = Math.max(3.5, (widthM * 100) / 7.5);

        // Midpoint for badge
        const midX = (source.x + target.x) / 2;
        const midY = (source.y + target.y) / 2;
        const badgeW = 44;
        const badgeH = 14;

        return (
          <Group
            key={edge.id}
            onClick={e => handleEdgeClick(edge.id, e)}
            onTap={e => handleEdgeClick(edge.id, e)}
          >
            {/* Selection Halo */}
            {isSelected && (
              <Line
                points={[source.x, source.y, target.x, target.y]}
                stroke="#ffffff"
                strokeWidth={baseWidth + 4}
                lineCap="round"
                opacity={0.85}
              />
            )}

            {/* Hit area for easy clicking */}
            <Line
              points={[source.x, source.y, target.x, target.y]}
              stroke="transparent"
              strokeWidth={Math.max(14, baseWidth + 8)}
              lineCap="round"
            />

            {/* Primary transmission media pathway line */}
            <Line
              points={[source.x, source.y, target.x, target.y]}
              stroke={mediaConfig.color}
              strokeWidth={baseWidth}
              dash={mediaConfig.dash}
              lineCap="round"
              opacity={count === 0 ? 0.75 : 0.95}
              shadowColor={mediaConfig.color}
              shadowBlur={isSelected ? 10 : 0}
              shadowOpacity={0.7}
            />

            {/* Inner fill indicator line when carrying cables */}
            {count > 0 && (
              <Line
                points={[source.x, source.y, target.x, target.y]}
                stroke={fillStatus.color}
                strokeWidth={Math.max(1.5, baseWidth * 0.35)}
                lineCap="round"
                opacity={0.95}
              />
            )}

            {/* Capacity fill badge pill at midpoint */}
            {count > 0 && stageScale > 0.45 && (
              <Group x={midX} y={midY} offsetX={badgeW / 2} offsetY={badgeH / 2}>
                <Rect
                  width={badgeW}
                  height={badgeH}
                  fill="rgba(10, 15, 28, 0.9)"
                  stroke={fillStatus.color}
                  strokeWidth={0.8}
                  cornerRadius={3}
                  shadowColor="#000000"
                  shadowBlur={4}
                  shadowOpacity={0.6}
                />
                <Circle x={6} y={badgeH / 2} radius={2.5} fill={fillStatus.color} />
                <Text
                  x={11}
                  y={2}
                  width={badgeW - 13}
                  text={`${count}/${maxCables}`}
                  fill="#ffffff"
                  fontSize={8.5}
                  fontFamily="JetBrains Mono, monospace"
                  fontStyle="bold"
                  align="center"
                  verticalAlign="middle"
                />
              </Group>
            )}
          </Group>
        );
      })}

      {/* Tray Junction Nodes */}
      {Object.values(trayNodes).map(node => (
        <Circle
          key={node.id}
          x={node.x}
          y={node.y}
          radius={5 / Math.max(0.5, stageScale)}
          hitStrokeWidth={18}
          fill={node.id === drawingTrayFromId ? '#ef4444' : '#64748b'}
          stroke="#0f172a"
          strokeWidth={1}
          shadowColor="#000"
          shadowBlur={2}
          shadowOpacity={0.5}
          onClick={e => handleNodeClick(node.id, e)}
          onTap={e => handleNodeClick(node.id, e)}
        />
      ))}

      {/* Ghost Line for active tray drawing */}
      {drawingTrayFromId && ghostPos && trayNodes[drawingTrayFromId] && (
        <Line
          points={[
            trayNodes[drawingTrayFromId].x,
            trayNodes[drawingTrayFromId].y,
            ghostPos.x,
            ghostPos.y,
          ]}
          stroke={activeMediaConfig.color}
          strokeWidth={3}
          dash={[6, 4]}
          opacity={0.85}
        />
      )}
    </Group>
  );
};